import type { Schemas } from '@kanos/api-client';
import { api, failure } from '@kanos/web-core';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

export type RespondentIntake = Schemas['RespondentIntake'];
export type ContractingParty = Schemas['ContractingParty'];
export type ContractingPartyInput = Schemas['ContractingPartyInput'];
export type BeneficialOwner = Schemas['BeneficialOwner'];
export type RespondentOwnerInput = Schemas['RespondentOwnerInput'];

const intakesKey = ['portal', 'intakes'] as const;
const caseKey = (caseId: string) => [...intakesKey, caseId] as const;
const path = (caseId: string) => ({ params: { path: { caseId } } });

/** The intakes the signed-in person answers for in this firm (ADR-026). */
export const intakesQuery = queryOptions({
  queryKey: intakesKey,
  queryFn: async (): Promise<RespondentIntake[]> => {
    const { data, error, response } = await api.GET('/api/v1/portal/intakes');
    if (!data) throw failure('listRespondentIntakes', error, response.status);
    return data;
  },
});

export const partyQuery = (caseId: string) =>
  queryOptions({
    queryKey: [...caseKey(caseId), 'party'] as const,
    queryFn: async (): Promise<ContractingParty> => {
      const { data, error, response } = await api.GET(
        '/api/v1/portal/intakes/{caseId}/party',
        path(caseId),
      );
      if (!data) throw failure('getOwnParty', error, response.status);
      return data;
    },
  });

export const ownersQuery = (caseId: string) =>
  queryOptions({
    queryKey: [...caseKey(caseId), 'owners'] as const,
    queryFn: async (): Promise<BeneficialOwner[]> => {
      const { data, error, response } = await api.GET(
        '/api/v1/portal/intakes/{caseId}/beneficial-owners',
        path(caseId),
      );
      if (!data) throw failure('listOwnOwners', error, response.status);
      return data;
    },
  });

/** A change of the intake's data; afterwards the person's intakes are read again. */
function useIntakeMutation<T, R>(mutationFn: (input: T) => Promise<R>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: intakesKey }),
  });
}

export const useStateParty = (caseId: string) =>
  useIntakeMutation(async (body: ContractingPartyInput) => {
    const { data, error, response } = await api.PUT('/api/v1/portal/intakes/{caseId}/party', {
      ...path(caseId),
      body,
    });
    if (!data) throw failure('stateOwnParty', error, response.status);
    return data;
  });

export const useStateOwner = (caseId: string) =>
  useIntakeMutation(
    async ({ body, idempotencyKey }: { body: RespondentOwnerInput; idempotencyKey: string }) => {
      const { data, error, response } = await api.POST(
        '/api/v1/portal/intakes/{caseId}/beneficial-owners',
        {
          params: { path: { caseId }, header: { 'Idempotency-Key': idempotencyKey } },
          body,
        },
      );
      if (!data) throw failure('stateOwnOwner', error, response.status);
      return data;
    },
  );

export const useReplaceOwner = (caseId: string) =>
  useIntakeMutation(async ({ ownerId, body }: { ownerId: string; body: RespondentOwnerInput }) => {
    const { data, error, response } = await api.PUT(
      '/api/v1/portal/intakes/{caseId}/beneficial-owners/{ownerId}',
      { params: { path: { caseId, ownerId } }, body },
    );
    if (!data) throw failure('replaceOwnOwner', error, response.status);
    return data;
  });

export const useRemoveOwner = (caseId: string) =>
  useIntakeMutation(async (ownerId: string) => {
    const { error, response } = await api.DELETE(
      '/api/v1/portal/intakes/{caseId}/beneficial-owners/{ownerId}',
      { params: { path: { caseId, ownerId } } },
    );
    if (!response.ok) throw failure('removeOwnOwner', error, response.status);
  });

/** Sends the data to the firm: it gets a notice, the intake keeps when. */
export const useSubmit = (caseId: string) =>
  useIntakeMutation(async () => {
    const { error, response } = await api.POST(
      '/api/v1/portal/intakes/{caseId}/submit',
      path(caseId),
    );
    if (!response.ok) throw failure('submitIntakeData', error, response.status);
  });
